(() => {
  const SELECTOR = '[data-gvs-compare-products]';
  const INSTANCE_KEY = '__gvsCompareProductsInstance';
  const STORAGE_VERSION = 2;

  const stringValue = (value) => value === null || value === undefined ? '' : String(value).trim();
  const escapeHtml = (value) => String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const stripHtml = (value) => {
    const element = document.createElement('div');
    element.innerHTML = String(value || '');
    return (element.textContent || '').trim();
  };

  const normaliseItem = (item = {}) => {
    const handle = stringValue(item.handle || item.productHandle || item.product_handle);
    const id = stringValue(item.id || item.productId || item.product_id || handle);
    if (!id || !handle) return null;

    return {
      version: STORAGE_VERSION,
      id,
      handle,
      url: stringValue(item.url || `/products/${handle}`),
      title: stringValue(item.title || item.name || 'Product'),
      image: stringValue(typeof item.image === 'string' ? item.image : item.image?.src || ''),
      variantId: stringValue(item.variantId || item.variant_id),
      variantExplicit: Boolean(item.variantExplicit || item.variant_explicit),
      addedAt: Number(item.addedAt || Date.now()),
    };
  };

  const cleanValue = (value) => {
    if (value === null || value === undefined || value === '' || value === '-') return '';
    if (Array.isArray(value)) return value.map(cleanValue).filter(Boolean);
    if (typeof value === 'object') return cleanValue(value.value || value.label || value.name || value.text || '');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return stripHtml(value);
  };

  const textValue = (value) => {
    const clean = cleanValue(value);
    return Array.isArray(clean) ? clean.join(', ') : clean;
  };

  const getPath = (object, path) => path.split('.').reduce((value, key) => value?.[key], object);

  const formatCents = (cents, currency = 'GBP') => {
    const numeric = Number(cents);
    if (!Number.isFinite(numeric)) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
      }).format(numeric / 100);
    } catch (_) {
      return `£${(numeric / 100).toFixed(2)}`;
    }
  };

  const normaliseSpecification = (value, type = '') => {
    const clean = textValue(value);
    if (!clean) return '';

    let output = clean.replace(/\s+/g, ' ').trim();

    if (type === 'motor') {
      output = output
        .replace(/(\d)\s*kw\b/gi, '$1 kW')
        .replace(/(\d)\s*w\b/gi, '$1 W');
    } else if (type === 'airflow') {
      output = output
        .replace(/\blpm\b/gi, 'L/min')
        .replace(/\bl\s*\/?\s*min(?:ute)?s?\b/gi, 'L/min')
        .replace(/\bm3\s*\/?\s*h\b/gi, 'm³/h')
        .replace(/(\d)\s*(L\/min|m³\/h)\b/g, '$1 $2');
    } else if (type === 'capacity') {
      output = output
        .replace(/(\d)\s*(?:litres?|liters?)\b/gi, '$1 L')
        .replace(/(\d)\s*l\b/gi, '$1 L');
    } else if (type === 'weight') {
      output = output.replace(/(\d)\s*kg\b/gi, '$1 kg');
    } else if (type === 'length') {
      output = output
        .replace(/(\d)\s*mm\b/gi, '$1 mm')
        .replace(/(\d)\s*cm\b/gi, '$1 cm')
        .replace(/(\d)\s*m\b/gi, '$1 m');
    }

    return output;
  };

  const configurationLines = (product) => {
    const options = Array.isArray(product?.variant?.options) ? product.variant.options : [];
    const lines = options.map((option, index) => {
      const label = cleanValue(option?.name) || `Option ${index + 1}`;
      const value = cleanValue(option?.value);
      if (!value || String(value).toLowerCase() === 'default title') return '';
      return `${label}: ${value}`;
    }).filter(Boolean);

    if (lines.length) return lines;
    const title = cleanValue(product?.variant?.title);
    return title && String(title).toLowerCase() !== 'default title' ? [title] : [];
  };

  const rowDefinitions = [
    { label: 'Selected configuration', shortLabel: 'Setup', getValue: configurationLines, primary: true },
    { label: 'Application', shortLabel: 'Application', path: 'custom.use_case', list: true, primary: true },
    { label: 'Equipment type', shortLabel: 'Type', path: 'product_type', primary: true },
    { label: 'System / range', shortLabel: 'Range', path: 'filters.system_range', list: true, primary: true },
    { label: 'Motor power', shortLabel: 'Motor', path: 'specs.motor_power', formatter: (value) => normaliseSpecification(value, 'motor') },
    { label: 'Airflow / suction', shortLabel: 'Airflow', path: 'specs.airflow', formatter: (value) => normaliseSpecification(value, 'airflow') },
    { label: 'Drum capacity', shortLabel: 'Capacity', path: 'specs.tank_capacity', formatter: (value) => normaliseSpecification(value, 'capacity') },
    { label: 'Reach height', shortLabel: 'Reach', path: 'specs.reach_height', formatter: (value) => normaliseSpecification(value, 'length') },
    { label: 'Weight', shortLabel: 'Weight', path: 'specs.weight', formatter: (value) => normaliseSpecification(value, 'weight') },
    { label: 'Connection size', shortLabel: 'Connection', path: 'filters.connection_size', list: true },
    { label: 'Connection type', shortLabel: 'Connection type', path: 'filters.connection_type', list: true },
    { label: 'Material', shortLabel: 'Material', path: 'filters.material', list: true },
    { label: 'Part type', shortLabel: 'Part type', path: 'filters.spare_part_type', list: true },
    { label: 'Nozzle style', shortLabel: 'Nozzle', path: 'filters.nozzle_style', list: true },
    { label: 'Neck type', shortLabel: 'Neck', path: 'filters.neck_type', list: true },
    { label: 'Neck angle', shortLabel: 'Angle', path: 'filters.neck_angle', list: true },
    { label: 'Warranty', shortLabel: 'Warranty', path: 'custom.warranty', detailOnly: true },
    { label: 'SKU', shortLabel: 'SKU', path: 'variant.sku', detailOnly: true },
    { label: 'Key specifications', shortLabel: 'Key specs', path: 'custom.key_specs', list: true, detailOnly: true },
  ];

  class GVSCompareProducts {
    constructor(root) {
      this.root = root;
      this.container = root.closest('.gvs-compare-products') || root;
      this.loading = root.querySelector('[data-gvs-compare-loading]');
      this.loadingText = root.querySelector('[data-gvs-compare-loading-text]');
      this.empty = root.querySelector('[data-gvs-compare-empty]');
      this.output = root.querySelector('[data-gvs-compare-output]');
      this.clearButton = this.container.querySelector('[data-gvs-compare-clear]');
      this.liveRegion = root.querySelector('[data-gvs-compare-live]');
      this.storageKey = root.dataset.storageKey || 'compareProducts';
      this.maxProducts = Number(root.dataset.maxProducts || 4);
      this.shopRoot = root.dataset.shopRoot || window.Shopify?.routes?.root || '/';
      this.dataSectionId = root.dataset.dataSectionId || 'product-compare-data';
      this.showPrice = root.dataset.showPrice === 'true';
      this.showAddToCart = root.dataset.showAddToCart === 'true';
      this.labels = {
        view: root.dataset.viewLabel || 'View product',
        add: root.dataset.addLabel || 'Add to basket',
        added: root.dataset.addedLabel || 'Added',
        unavailable: root.dataset.unavailableLabel || 'Unavailable',
        remove: root.dataset.removeLabel || 'Remove',
        differences: root.dataset.differencesLabel || 'Show differences only',
        all: root.dataset.allLabel || 'Show all details',
        share: root.dataset.shareLabel || 'Copy comparison link',
        shared: root.dataset.sharedLabel || 'Link copied',
      };
      this.products = [];
      this.identities = [];
      this.showAllDetails = false;
      this.request = 0;
      this.abortController = null;
      this.onClick = this.onClick.bind(this);
      this.render = this.render.bind(this);
      this.importSharedSelection();
      this.container.addEventListener('click', this.onClick);
      window.addEventListener('storage', this.render);
      window.addEventListener('gvs:compare-updated', this.render);
      window.addEventListener('compare:updated', this.render);
      this.render();
    }

    destroy() {
      this.abortController?.abort();
      this.container.removeEventListener('click', this.onClick);
      window.removeEventListener('storage', this.render);
      window.removeEventListener('gvs:compare-updated', this.render);
      window.removeEventListener('compare:updated', this.render);
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      setTimeout(() => { this.liveRegion.textContent = message; }, 30);
    }

    readProducts() {
      try {
        const parsed = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(parsed)) return [];
        const seen = new Set();
        return parsed
          .map(normaliseItem)
          .filter(Boolean)
          .filter((item) => {
            const key = item.id || item.handle;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, Number.isFinite(this.maxProducts) ? this.maxProducts : 4);
      } catch (_) {
        return [];
      }
    }

    writeProducts(products) {
      const normalised = products.map(normaliseItem).filter(Boolean).slice(0, this.maxProducts);
      try {
        if (normalised.length) localStorage.setItem(this.storageKey, JSON.stringify(normalised));
        else localStorage.removeItem(this.storageKey);
      } catch (_) {
        this.announce('Your comparison could not be saved in this browser.');
        return;
      }

      window.dispatchEvent(new CustomEvent('gvs:compare-updated', { detail: { products: normalised } }));
    }

    importSharedSelection() {
      const encoded = new URLSearchParams(location.search).get('compare');
      if (!encoded) return;

      const imported = encoded.split(',').slice(0, this.maxProducts).map((token) => {
        const [handle, variantId = ''] = token.split('~');
        let cleanHandle = '';
        let cleanVariantId = '';
        try {
          cleanHandle = decodeURIComponent(handle || '').trim();
          cleanVariantId = decodeURIComponent(variantId || '').trim();
        } catch (_) {
          return null;
        }
        if (!cleanHandle) return null;
        return normaliseItem({
          id: cleanHandle,
          handle: cleanHandle,
          url: `/products/${cleanHandle}`,
          title: cleanHandle.replace(/-/g, ' '),
          variantId: cleanVariantId,
          variantExplicit: Boolean(cleanVariantId),
        });
      }).filter(Boolean);

      if (imported.length) {
        try { localStorage.setItem(this.storageKey, JSON.stringify(imported)); } catch (_) {}
      }
    }

    setLoading(isLoading, message = '') {
      this.root.setAttribute('aria-busy', String(isLoading));
      if (this.loading) this.loading.hidden = !isLoading;
      if (this.loadingText && message) this.loadingText.textContent = message;
    }

    async render() {
      const request = ++this.request;
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.identities = this.readProducts();
      if (this.clearButton) this.clearButton.hidden = this.identities.length === 0;

      if (!this.identities.length) {
        this.setLoading(false);
        this.products = [];
        if (this.output) this.output.innerHTML = '';
        if (this.empty) this.empty.hidden = false;
        return;
      }

      if (this.empty) this.empty.hidden = true;
      if (this.output) this.output.innerHTML = '';
      this.setLoading(true, `Refreshing 0 of ${this.identities.length} products…`);

      const products = [];
      let completed = 0;
      try {
        await Promise.all(this.identities.map(async (identity) => {
          const product = await this.fetchProduct(identity, this.abortController.signal);
          products.push({ ...product, _identity: identity });
          completed += 1;
          if (this.loadingText) this.loadingText.textContent = `Refreshing ${completed} of ${this.identities.length} products…`;
        }));
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('GVS compare: live refresh failed.', error);
      }

      if (request !== this.request) return;
      this.products = this.identities.map((identity) => products.find((product) =>
        String(product.id) === String(identity.id) || product.handle === identity.handle
      ) || this.unavailableProduct(identity));

      this.refreshStoredSnapshots();
      this.setLoading(false);
      if (this.output) this.output.innerHTML = this.buildComparison();
    }

    productUrl(handle, suffix = '') {
      const cleanRoot = String(this.shopRoot || '/').replace(/\/+$/, '');
      const encodedHandle = encodeURIComponent(String(handle || '').trim());
      return `${cleanRoot}/products/${encodedHandle}${suffix}`;
    }

    async fetchProduct(identity, signal) {
      const requests = await Promise.allSettled([
        this.fetchCoreProduct(identity, signal),
        this.fetchEnrichedProduct(identity, signal),
      ]);

      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      const core = requests[0].status === 'fulfilled' ? requests[0].value : null;
      const enriched = requests[1].status === 'fulfilled' ? requests[1].value : null;

      if (!core && !enriched) {
        const reasons = requests
          .filter((result) => result.status === 'rejected')
          .map((result) => result.reason?.message)
          .filter(Boolean)
          .join('; ');
        console.warn(`GVS compare: live data unavailable for ${identity.handle}. ${reasons}`);
        return this.unavailableProduct(identity);
      }

      const merged = {
        ...(core || {}),
        ...(enriched || {}),
        price: { ...(core?.price || {}), ...(enriched?.price || {}) },
        variant: { ...(core?.variant || {}), ...(enriched?.variant || {}) },
        specs: { ...(core?.specs || {}), ...(enriched?.specs || {}) },
        filters: { ...(core?.filters || {}), ...(enriched?.filters || {}) },
        custom: { ...(core?.custom || {}), ...(enriched?.custom || {}) },
        live: true,
        fallback: !enriched,
      };

      merged.id = merged.id || identity.id;
      merged.handle = merged.handle || identity.handle;
      merged.url = merged.url || identity.url || this.productUrl(identity.handle);
      merged.title = merged.title || identity.title;
      merged.image = merged.image || identity.image;
      merged.available = Boolean(merged.variant?.available ?? merged.available);

      return merged;
    }

    async fetchCoreProduct(identity, signal) {
      const url = new URL(this.productUrl(identity.handle, '.js'), location.origin);
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
        credentials: 'same-origin',
        signal,
      });

      if (!response.ok) throw new Error(`Shopify product JSON returned ${response.status}`);
      const data = await response.json();
      if (!data || (!data.id && !data.handle)) throw new Error('Shopify product JSON was empty');

      const variants = Array.isArray(data.variants) ? data.variants : [];
      const variant = variants.find((item) => String(item.id) === String(identity.variantId))
        || variants.find((item) => item.available)
        || variants[0]
        || {};
      const incVat = Number(variant.price ?? data.price ?? 0);
      const isTaxable = variant.taxable !== false;
      const exVat = isTaxable ? Math.round((incVat * 100) / 120) : incVat;
      const currency = window.Shopify?.currency?.active || 'GBP';
      const image = variant.featured_image?.src
        || variant.featured_image
        || data.featured_image
        || data.images?.[0]
        || identity.image;
      const selectedOptions = Array.isArray(variant.options)
        ? variant.options.map((value, index) => ({ name: data.options?.[index] || `Option ${index + 1}`, value }))
        : [];
      const productUrl = identity.variantId
        ? `${this.productUrl(data.handle || identity.handle)}?variant=${encodeURIComponent(identity.variantId)}`
        : this.productUrl(data.handle || identity.handle);

      return {
        id: data.id || identity.id,
        handle: data.handle || identity.handle,
        url: productUrl,
        title: data.title || identity.title,
        vendor: data.vendor || '',
        product_type: data.type || '',
        description: stripHtml(data.description || ''),
        image: typeof image === 'string' ? image : image?.src || identity.image,
        available: Boolean(variant.available ?? data.available),
        has_only_default_variant: variants.length <= 1,
        variant: {
          id: variant.id || identity.variantId,
          title: variant.title && variant.title !== 'Default Title' ? variant.title : '',
          sku: variant.sku || '',
          available: Boolean(variant.available),
          taxable: isTaxable,
          options: selectedOptions,
        },
        price: {
          currency,
          ex_vat_cents: exVat,
          inc_vat_cents: incVat,
          ex_vat_formatted: formatCents(exVat, currency),
          inc_vat_formatted: formatCents(incVat, currency),
        },
        specs: {},
        filters: {},
        custom: {},
      };
    }

    async fetchEnrichedProduct(identity, signal) {
      const url = new URL(this.productUrl(identity.handle), location.origin);
      url.searchParams.set('section_id', this.dataSectionId);
      if (identity.variantId) url.searchParams.set('variant', identity.variantId);

      const response = await fetch(url, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
        credentials: 'same-origin',
        signal,
      });
      if (!response.ok) throw new Error(`Comparison data section returned ${response.status}`);

      const text = await response.text();
      const documentFragment = new DOMParser().parseFromString(text, 'text/html');
      const script = documentFragment.querySelector('[data-gvs-compare-data]');
      const payload = script?.textContent?.trim() || text.trim();
      if (!payload) throw new Error('Comparison data section was empty');

      const data = JSON.parse(payload);
      if (!data || (!data.id && !data.handle)) throw new Error('Comparison data section contained no product');
      return data;
    }

    unavailableProduct(identity) {
      return {
        id: identity.id,
        handle: identity.handle,
        url: identity.url,
        title: identity.title,
        image: identity.image,
        available: false,
        variant: { id: identity.variantId, title: '', sku: '', available: false },
        price: {}, specs: {}, filters: {}, custom: {},
        live: false,
        fallback: false,
        unavailableReason: 'Live product details are currently unavailable.',
        _identity: identity,
      };
    }

    refreshStoredSnapshots() {
      const refreshed = this.identities.map((identity) => {
        const product = this.products.find((item) => String(item.id) === String(identity.id) || item.handle === identity.handle);
        if (!product?.live) return identity;
        return {
          ...identity,
          id: String(product.id || identity.id),
          handle: product.handle || identity.handle,
          url: product.url || identity.url,
          title: product.title || identity.title,
          image: product.image_small || product.image || identity.image,
          variantId: String(product.variant?.id || identity.variantId || ''),
        };
      });

      try { localStorage.setItem(this.storageKey, JSON.stringify(refreshed)); } catch (_) {}
    }

    buildComparison() {
      const rows = this.visibleRows();
      const count = this.products.length;
      const productCells = this.products.map((product) => this.productCell(product)).join('');
      const rowCells = rows.length
        ? rows.map((row) => this.rowCells(row)).join('')
        : `<div class="gvs-compare-products__no-differences" role="row"><div role="cell">The selected products do not currently have any populated specification differences to show.</div></div>`;

      return `
        <div class="gvs-compare-products__board">
          <div class="gvs-compare-products__summary">
            <div class="gvs-compare-products__summary-copy">
              <span class="gvs-compare-products__count"><strong>${count}</strong> ${count === 1 ? 'product' : 'products'} selected</span>
              <span class="gvs-compare-products__scroll-hint">Live prices refreshed. Swipe or scroll sideways to review each product.</span>
            </div>
            <div class="gvs-compare-products__summary-actions">
              <button type="button" class="gvs-compare-products__utility" data-gvs-compare-differences aria-pressed="${this.showAllDetails}">${escapeHtml(this.showAllDetails ? this.labels.differences : this.labels.all)}</button>
              <button type="button" class="gvs-compare-products__utility" data-gvs-compare-share>${escapeHtml(this.labels.share)}</button>
            </div>
          </div>

          <div class="gvs-compare-products__scroller" tabindex="0" role="region" aria-label="Product comparison table. Scroll horizontally to review every product.">
            <div class="gvs-compare-products__grid" style="--gvs-compare-count: ${count};" role="table" aria-colcount="${count + 1}">
              <div class="gvs-compare-products__row gvs-compare-products__row--products" role="row">
                <div class="gvs-compare-products__cell gvs-compare-products__cell--feature gvs-compare-products__cell--product-label" role="columnheader">
                  <span class="gvs-compare-products__feature-label"><span class="gvs-compare-products__feature-label-full">Selected product</span><span class="gvs-compare-products__feature-label-short">Product</span></span>
                </div>
                ${productCells}
              </div>
              ${rowCells}
            </div>
          </div>
        </div>
      `;
    }

    productCell(product) {
      const identity = product._identity || {};
      const url = product.url || identity.url || `/products/${product.handle}`;
      const title = product.title || identity.title || 'Product';
      const image = product.image || identity.image;
      const id = String(product.id || identity.id || product.handle);
      const availability = product.live ? (product.available ? 'In stock' : 'Out of stock') : 'Check availability';
      const availabilityClass = product.live && product.available ? 'gvs-compare-products__pill--stock' : 'gvs-compare-products__pill--out';
      const priceInc = product.price?.inc_vat_formatted || '';
      const priceEx = product.price?.ex_vat_formatted || '';
      const type = textValue(product.product_type);
      const canAdd = this.showAddToCart && product.live && product.available && product.variant?.id && (product.has_only_default_variant || identity.variantExplicit);

      const imageMarkup = image
        ? `<img class="gvs-compare-products__image" src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy" decoding="async">`
        : `<span class="gvs-compare-products__placeholder">No image</span>`;

      const addAction = canAdd
        ? `<button type="button" class="gvs-compare-products__product-button gvs-compare-products__product-button--basket" data-gvs-compare-add data-product-id="${escapeHtml(id)}" data-variant-id="${escapeHtml(product.variant.id)}">${escapeHtml(this.labels.add)}</button>`
        : product.live && product.available && !product.has_only_default_variant
          ? `<a class="gvs-compare-products__product-button gvs-compare-products__product-button--options" href="${escapeHtml(url)}">Choose options</a>`
          : '';

      return `
        <article class="gvs-compare-products__cell gvs-compare-products__product-card${product.live ? '' : ' is-unavailable'}" role="columnheader">
          <button type="button" class="gvs-compare-products__remove" data-gvs-compare-remove="${escapeHtml(id)}" aria-label="${escapeHtml(this.labels.remove)} ${escapeHtml(title)}"><span aria-hidden="true">×</span></button>
          <a class="gvs-compare-products__image-wrap" href="${escapeHtml(url)}" aria-label="View ${escapeHtml(title)}">${imageMarkup}</a>
          <div class="gvs-compare-products__product-copy">
            ${type ? `<span class="gvs-compare-products__type">${escapeHtml(type)}</span>` : ''}
            <h3 class="gvs-compare-products__title"><a href="${escapeHtml(url)}">${escapeHtml(title)}</a></h3>
            ${product.unavailableReason ? `<p class="gvs-compare-products__data-warning">${escapeHtml(product.unavailableReason)}</p>` : product.fallback ? `<p class="gvs-compare-products__data-note">Core details refreshed; specification data is limited.</p>` : ''}
          </div>
          <div class="gvs-compare-products__price-block">
            ${priceInc ? `<strong>${escapeHtml(priceInc)} <span>inc VAT</span></strong>` : '<strong>Price unavailable</strong>'}
            ${priceEx ? `<small>${escapeHtml(priceEx)} ex VAT</small>` : ''}
          </div>
          <span class="gvs-compare-products__pill ${availabilityClass}">${escapeHtml(availability)}</span>
          <div class="gvs-compare-products__product-actions">
            ${addAction}
            <a class="gvs-compare-products__product-button gvs-compare-products__product-button--view" href="${escapeHtml(url)}">${escapeHtml(this.labels.view)}</a>
          </div>
        </article>
      `;
    }

    visibleRows() {
      return rowDefinitions.map((definition) => {
        const values = this.products.map((product) => {
          const sourceValue = definition.getValue
            ? definition.getValue(product)
            : getPath(product, definition.path);
          return definition.formatter ? definition.formatter(sourceValue) : cleanValue(sourceValue);
        });
        const populated = values.some((value) => Array.isArray(value) ? value.length : Boolean(value));
        const comparisonValues = values.map((value) => {
          const normalised = Array.isArray(value)
            ? value.join('|').toLowerCase()
            : String(value || '').toLowerCase();
          return normalised || '__gvs_empty__';
        });
        const different = populated && new Set(comparisonValues).size > 1;
        return { ...definition, values, populated, different };
      }).filter((row) => {
        if (!row.populated) return false;
        if (this.showAllDetails) return true;
        if (row.detailOnly) return false;
        return row.primary || row.different;
      });
    }

    valueMarkup(value) {
      const clean = cleanValue(value);
      if (!clean || (Array.isArray(clean) && !clean.length)) return '<span class="is-empty">—</span>';
      if (Array.isArray(clean)) {
        return `<ul>${clean.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
      }
      return escapeHtml(clean);
    }

    rowCells(row) {
      const cells = row.values.map((value) => `
        <div class="gvs-compare-products__cell gvs-compare-products__cell--value" role="cell">
          <div class="gvs-compare-products__value">${this.valueMarkup(value)}</div>
        </div>
      `).join('');

      return `
        <div class="gvs-compare-products__row${row.different ? ' gvs-compare-products__row--different' : ''}" role="row">
          <div class="gvs-compare-products__cell gvs-compare-products__cell--feature" role="rowheader">
            <span class="gvs-compare-products__feature-label"><span class="gvs-compare-products__feature-label-full">${escapeHtml(row.label)}</span><span class="gvs-compare-products__feature-label-short">${escapeHtml(row.shortLabel || row.label)}</span>${row.different ? '<span class="gvs-compare-products__difference-mark">Different</span>' : ''}</span>
          </div>
          ${cells}
        </div>
      `;
    }

    async onClick(event) {
      if (!(event.target instanceof Element)) return;
      const removeButton = event.target.closest('[data-gvs-compare-remove]');
      if (removeButton) {
        const removeId = removeButton.getAttribute('data-gvs-compare-remove');
        const products = this.readProducts().filter((product) => product.id !== removeId && product.handle !== removeId);
        this.writeProducts(products);
        this.announce('Product removed from comparison.');
        return;
      }

      if (event.target.closest('[data-gvs-compare-clear]')) {
        this.writeProducts([]);
        this.announce('Comparison cleared.');
        return;
      }

      if (event.target.closest('[data-gvs-compare-differences]')) {
        const currentScroller = this.output?.querySelector('.gvs-compare-products__scroller');
        const scrollLeft = currentScroller?.scrollLeft || 0;
        this.showAllDetails = !this.showAllDetails;
        if (this.output) {
          this.output.innerHTML = this.buildComparison();
          requestAnimationFrame(() => {
            const nextScroller = this.output?.querySelector('.gvs-compare-products__scroller');
            if (nextScroller) nextScroller.scrollLeft = scrollLeft;
          });
        }
        this.announce(this.showAllDetails ? 'All comparison details are now shown.' : 'The comparison is now focused on key details and differences.');
        return;
      }

      const shareButton = event.target.closest('[data-gvs-compare-share]');
      if (shareButton) {
        await this.copyShareLink(shareButton);
        return;
      }

      const addButton = event.target.closest('[data-gvs-compare-add]');
      if (addButton) await this.addToCart(addButton);
    }

    async copyShareLink(button) {
      const tokens = this.identities.map((item) => `${encodeURIComponent(item.handle)}~${encodeURIComponent(item.variantId || '')}`);
      const url = new URL(location.href);
      url.searchParams.set('compare', tokens.join(','));
      const original = button.textContent;

      try {
        await navigator.clipboard.writeText(url.toString());
        button.textContent = this.labels.shared;
        this.announce('Comparison link copied.');
      } catch (_) {
        window.prompt('Copy this comparison link:', url.toString());
      }

      setTimeout(() => { button.textContent = original; }, 1800);
    }

    async addToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      const productId = button.getAttribute('data-product-id');
      if (!variantId) return;

      const original = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((item) => item.dataset.sectionId)
          .filter(Boolean);
        if (sectionIds.length) formData.append('sections', [...new Set(sectionIds)].join(','));

        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'text/html' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to basket failed');

        button.textContent = this.labels.added;
        button.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: variantId,
            data: {
              source: 'product-form-component',
              itemCount: 1,
              productId,
              variantId,
              sections: data.sections,
            },
          },
        }));
        this.announce('Product added to basket.');
      } catch (error) {
        button.textContent = this.labels.unavailable;
        this.announce(error.message || 'This product could not be added to the basket.');
      }

      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }, 1800);
    }
  }

  const initialiseRoot = (root) => {
    if (!root || root[INSTANCE_KEY]) return;
    root[INSTANCE_KEY] = new GVSCompareProducts(root);
    root.dataset.gvsCompareInitialised = 'true';
  };

  const init = (scope = document) => scope.querySelectorAll?.(SELECTOR).forEach(initialiseRoot);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.(SELECTOR).forEach((root) => {
      root[INSTANCE_KEY]?.destroy();
      root[INSTANCE_KEY] = null;
    });
  });
})();
